import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail, CheckCircle, Clock, MapPin, Copy, Check,
  Zap, Film, ExternalLink, X, ChevronDown, Plus,
  BarChart3, Building2, Globe,
  Target, TrendingUp, Info, Search, AlertTriangle,
  Wifi, WifiOff, Video, VideoOff, RefreshCw, ScanLine, Trash2,
} from 'lucide-react';
import { db, functions } from '../firebase';
import {
  collection, doc, getDocs, setDoc, updateDoc,
  addDoc, serverTimestamp, deleteDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  BUSINESS_TYPES,
  LOCAL_STATUS_CONFIG,
  PRICING_TIERS,
  LOCAL_BUSINESS_SEED,
  opportunityScore,
  scoreLabel,
  generateLocalPitchEmail,
} from '../localBusinessData';

// ─── Firebase callables ───────────────────────────────────────────────────────
const analyzeWebsiteFn       = httpsCallable(functions, 'analyzeWebsite');
const scanNearbyBusinessesFn = httpsCallable(functions, 'scanNearbyBusinesses', { timeout: 120000 });
const findBusinessEmailFn    = httpsCallable(functions, 'findBusinessEmail');

// ─── Constants ─────────────────────────────────────────────────────────────────
const DAILY_GOAL = 5;
const TODAY_KEY = `bmf_local_outreach_${new Date().toISOString().slice(0, 10)}`;
const FILTERS = ['all', 'pending', 'sent', 'replied', 'booked', 'declined', 'noResponse'];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getTodayCount() { return parseInt(localStorage.getItem(TODAY_KEY) || '0', 10); }
function incrementTodayCount() {
  const n = getTodayCount() + 1;
  localStorage.setItem(TODAY_KEY, String(n));
  return n;
}
function uid() { return `lb-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`; }

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-5 flex items-center gap-4">
      <div className={`p-3 rounded-xl ${color} bg-opacity-10`}>
        <Icon size={20} className={color.replace('bg-', 'text-')} />
      </div>
      <div>
        <p className="text-2xl font-black text-white">{value}</p>
        <p className="text-xs tracking-widest uppercase text-neutral-500">{label}</p>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = LOCAL_STATUS_CONFIG[status] || LOCAL_STATUS_CONFIG.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold tracking-widest uppercase border ${cfg.color} ${cfg.bg} ${cfg.border}`}>
      {cfg.label}
    </span>
  );
}

function OpportunityBadge({ biz }) {
  const score = opportunityScore(biz);
  const info = scoreLabel(score);
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold border ${info.color} ${info.bg} ${info.border}`}>
      <Target size={10} /> {score}/10
    </span>
  );
}

function ScoreStars({ value }) {
  if (value === null || value === undefined) return <span className="text-neutral-600 text-xs italic">not rated</span>;
  return (
    <span className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className={i < value ? 'text-amber-400' : 'text-neutral-700'}>★</span>
      ))}
    </span>
  );
}

// ─── Gap Report ───────────────────────────────────────────────────────────────

function GapChip({ label, present, icon: Icon }) {
  return (
    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium ${
      present
        ? 'border-emerald-400/20 bg-emerald-400/5 text-emerald-400'
        : 'border-red-400/30 bg-red-400/5 text-red-400'
    }`}>
      {present
        ? <Check size={11} className="text-emerald-400" />
        : <X size={11} className="text-red-400" />
      }
      {Icon && <Icon size={11} />}
      {label}
    </div>
  );
}

function GapReport({ gaps, url }) {
  if (!gaps) return null;

  if (!gaps.fetchSuccess) {
    return (
      <div className="mb-4 p-4 bg-red-400/5 border border-red-400/20 rounded-xl">
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle size={14} className="text-red-400" />
          <span className="text-xs font-bold text-red-400 uppercase tracking-widest">Analysis Failed</span>
        </div>
        <p className="text-xs text-neutral-400">{gaps.errorMsg || 'Could not reach the website.'}</p>
        <p className="text-xs text-neutral-600 mt-1">The email below uses your manually entered info instead.</p>
      </div>
    );
  }

  const missingCount = [
    !gaps.hasAnyVideo,
    !gaps.hasYouTubeLink,
    !gaps.hasInstagramLink,
    !gaps.hasTikTokLink,
    !gaps.hasFacebookLink,
    gaps.isOutdated,
    !gaps.hasMobileViewport,
  ].filter(Boolean).length;

  return (
    <div className="mb-6 border border-neutral-800 rounded-2xl overflow-hidden">
      <div className="bg-neutral-900 px-4 py-3 border-b border-neutral-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Search size={14} className="text-amber-400" />
          <span className="text-sm font-bold">Website Analysis</span>
          {url && <a href={url} target="_blank" rel="noreferrer" className="text-xs text-neutral-600 hover:text-amber-400 flex items-center gap-1 transition-colors"><ExternalLink size={10} />{url.replace(/^https?:\/\//, '').slice(0, 40)}</a>}
        </div>
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
          missingCount >= 4 ? 'text-red-400 bg-red-400/10' :
          missingCount >= 2 ? 'text-amber-400 bg-amber-400/10' :
          'text-emerald-400 bg-emerald-400/10'
        }`}>
          {missingCount} gap{missingCount !== 1 ? 's' : ''} found
        </span>
      </div>

      <div className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
        <GapChip label="Video on site"      present={gaps.hasAnyVideo}       icon={gaps.hasAnyVideo ? Video : VideoOff} />
        <GapChip label="YouTube"            present={gaps.hasYouTubeLink || gaps.hasYouTubeEmbed} />
        <GapChip label="Instagram linked"   present={gaps.hasInstagramLink}   />
        <GapChip label="TikTok linked"      present={gaps.hasTikTokLink}      />
        <GapChip label="Facebook linked"    present={gaps.hasFacebookLink}    />
        <GapChip label="Mobile-optimized"   present={gaps.hasMobileViewport}  icon={gaps.hasMobileViewport ? Wifi : WifiOff} />
        <GapChip
          label={gaps.isOutdated ? `Site ~${gaps.copyrightYear} design` : 'Site is current'}
          present={!gaps.isOutdated}
        />
        <GapChip label="Online booking"     present={gaps.hasBooking}         />
        <GapChip label="Gallery / photos"   present={gaps.hasGallery || gaps.imageCount > 5} />
      </div>

      {missingCount > 0 && (
        <div className="px-4 pb-4">
          <p className="text-xs text-amber-400 font-medium">
            ↑ Each red gap is called out specifically in the email below. The more gaps, the more personalized and compelling the pitch.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Business Card ────────────────────────────────────────────────────────────

function BusinessCard({ biz, onOpen }) {
  const typeInfo = BUSINESS_TYPES[biz.type] || BUSINESS_TYPES.other;
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="bg-neutral-900 border border-neutral-800 rounded-2xl p-5 hover:border-neutral-600 transition-colors cursor-pointer group"
      onClick={() => onOpen(biz)}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-base">{typeInfo.emoji}</span>
            <h3 className="font-black text-white text-base leading-tight truncate group-hover:text-amber-400 transition-colors">
              {biz.name}
            </h3>
          </div>
          <span className="flex items-center gap-1 text-xs text-neutral-500">
            <MapPin size={11} /> {biz.address}
            {biz.distanceMiles ? ` · ${biz.distanceMiles}mi` : ''}
          </span>
        </div>
        <StatusBadge status={biz.status} />
      </div>

      <div className="grid grid-cols-2 gap-2 mb-4">
        <div className="bg-neutral-950 rounded-xl px-3 py-2">
          <p className="text-xs text-neutral-600 flex items-center gap-1 mb-1"><Globe size={10} /> Website</p>
          {biz.website
            ? <ScoreStars value={biz.webScore} />
            : <span className="text-xs text-red-400 font-medium">No website</span>
          }
        </div>
        <div className="bg-neutral-950 rounded-xl px-3 py-2">
          <p className="text-xs text-neutral-600 flex items-center gap-1 mb-1"><Globe size={10} /> Social</p>
          {(biz.instagram || biz.facebook)
            ? <ScoreStars value={biz.socialScore} />
            : <span className="text-xs text-red-400 font-medium">No social</span>
          }
        </div>
      </div>

      <div className="flex items-center justify-between">
        <OpportunityBadge biz={biz} />
        <span className="text-xs text-neutral-600 group-hover:text-neutral-400 transition-colors">
          Click to manage →
        </span>
      </div>
    </motion.div>
  );
}

// ─── Daily Queue ──────────────────────────────────────────────────────────────

function DailyQueue({ businesses, onOpen, todayCount }) {
  const queue = businesses
    .filter((b) => b.status === 'pending')
    .sort((a, b) => opportunityScore(b) - opportunityScore(a))
    .slice(0, DAILY_GOAL);

  if (queue.length === 0) return null;

  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-black flex items-center gap-2">
            <Zap size={18} className="text-amber-400" /> Today's Queue
          </h2>
          <p className="text-xs text-neutral-500 mt-0.5">
            Top {DAILY_GOAL} prospects by opportunity score — {todayCount >= DAILY_GOAL
              ? <span className="text-emerald-400 font-bold">Daily goal hit!</span>
              : <span>{Math.max(0, DAILY_GOAL - todayCount)} pitches to go today</span>
            }
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-32 h-2 bg-neutral-800 rounded-full overflow-hidden">
            <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${Math.min(100, (todayCount / DAILY_GOAL) * 100)}%` }} />
          </div>
          <span className="text-xs text-neutral-500 whitespace-nowrap">{todayCount}/{DAILY_GOAL}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
        {queue.map((biz) => {
          const score = opportunityScore(biz);
          const info = scoreLabel(score);
          const typeInfo = BUSINESS_TYPES[biz.type] || BUSINESS_TYPES.other;
          return (
            <button
              key={biz.id}
              onClick={() => onOpen(biz)}
              className="text-left bg-neutral-950 border border-amber-400/20 hover:border-amber-400/50 rounded-2xl p-4 transition-all group"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-lg">{typeInfo.emoji}</span>
                <span className={`text-xs font-bold ${info.color}`}>{score}/10</span>
              </div>
              <p className="font-bold text-sm text-white group-hover:text-amber-400 transition-colors truncate">{biz.name}</p>
              <p className="text-xs text-neutral-600 truncate mt-0.5">{biz.address}</p>
              <p className="text-xs text-amber-400/60 mt-2 font-medium">Analyze & Pitch →</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Pricing Panel ────────────────────────────────────────────────────────────

function PricingPanel() {
  return (
    <div className="mt-6 border border-neutral-800 rounded-2xl overflow-hidden">
      <div className="bg-neutral-900 px-5 py-3 border-b border-neutral-800">
        <p className="text-xs tracking-widest uppercase text-neutral-500 font-medium">Monthly Retainer Packages</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-neutral-800">
        {PRICING_TIERS.map((tier) => (
          <div key={tier.name} className={`p-5 ${tier.highlight ? 'bg-amber-400/5' : ''}`}>
            <div className="flex items-center justify-between mb-1">
              <p className={`text-sm font-black ${tier.highlight ? 'text-amber-400' : 'text-white'}`}>{tier.name}</p>
              {tier.highlight && <span className="text-xs bg-amber-400 text-black font-bold px-2 py-0.5 rounded-full">Most Popular</span>}
            </div>
            <p className="text-2xl font-black text-white mb-0.5">{tier.price}<span className="text-sm font-normal text-neutral-500">{tier.period}</span></p>
            <p className="text-xs text-neutral-500 mb-3 leading-relaxed">{tier.description}</p>
            <ul className="space-y-1.5">
              {tier.deliverables.map((d) => (
                <li key={d} className="flex items-start gap-1.5 text-xs text-neutral-300">
                  <span className="text-amber-400 mt-0.5 shrink-0">•</span> {d}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Add Business Modal ───────────────────────────────────────────────────────

const EMPTY_FORM = {
  name: '', type: 'restaurant', address: '', distanceMiles: '',
  website: '', noWebsite: false,
  instagram: '', facebook: '', noSocial: false,
  webScore: 3, socialScore: 3,
  contactName: '', contactEmail: '', notes: '',
};

function ScoreRadio({ label, value, onChange, disabled }) {
  return (
    <div>
      <p className="text-xs text-neutral-500 uppercase tracking-widest mb-1.5">{label}</p>
      {disabled
        ? <span className="text-xs text-neutral-600 italic">N/A</span>
        : <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onClick={() => onChange(n)}
                className={`w-8 h-8 rounded-lg text-sm font-bold transition-all border ${
                  value === n ? 'bg-amber-400 text-black border-amber-400' : 'bg-neutral-900 text-neutral-500 border-neutral-700 hover:border-neutral-500'
                }`}>
                {n}
              </button>
            ))}
            <p className="text-xs text-neutral-700 self-end pb-1 ml-1">1=bad · 5=great</p>
          </div>
      }
    </div>
  );
}

// ─── Google Maps Scan Modal ───────────────────────────────────────────────────

function ScanModal({ onClose, onScanComplete }) {
  const [businessType, setBusinessType] = useState('restaurant');
  const [radiusMiles, setRadiusMiles] = useState(30);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null); // { added, skipped, total, businesses }
  const [error, setError] = useState(null);

  async function handleScan() {
    setScanning(true);
    setResult(null);
    setError(null);
    try {
      const res = await scanNearbyBusinessesFn({ businessType, radiusMiles, maxResults: 20 });
      setResult(res.data);
      onScanComplete();
    } catch (err) {
      console.error('Scan error:', err);
      setError(err.message || 'Scan failed. Check that GOOGLE_PLACES_KEY secret is set.');
    }
    setScanning(false);
  }

  const inputCls = "w-full bg-neutral-900 border border-neutral-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-amber-400 transition-colors";

  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div className="min-h-full max-w-xl mx-auto px-4 sm:px-6 py-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <span className="text-xs tracking-[0.3em] uppercase text-amber-400 font-medium">Auto-Discovery</span>
            <h2 className="text-2xl font-black mt-1 flex items-center gap-2">
              <ScanLine size={22} className="text-amber-400" /> Scan Google Maps
            </h2>
            <p className="text-sm text-neutral-400 mt-1">
              Finds real businesses near Yardley, PA — pulls their name, address, and website directly from Google.
            </p>
          </div>
          <button onClick={onClose} className="shrink-0 p-2 rounded-full bg-neutral-800 hover:bg-neutral-700 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Settings */}
        <div className="space-y-5 mb-6">
          <div>
            <label className="block text-xs text-neutral-500 uppercase tracking-widest mb-1.5">Business Type to Search</label>
            <select value={businessType} onChange={(e) => setBusinessType(e.target.value)} className={inputCls}>
              {Object.entries(BUSINESS_TYPES).map(([k, v]) => (
                <option key={k} value={k}>{v.emoji} {v.label}</option>
              ))}
            </select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs text-neutral-500 uppercase tracking-widest">Search Radius</label>
              <span className="text-sm font-bold text-amber-400">{radiusMiles} miles</span>
            </div>
            <input
              type="range" min="5" max="30" step="5"
              value={radiusMiles}
              onChange={(e) => setRadiusMiles(Number(e.target.value))}
              className="w-full accent-amber-400"
            />
            <div className="flex justify-between text-xs text-neutral-600 mt-1">
              <span>5mi</span><span>10mi</span><span>15mi</span><span>20mi</span><span>25mi</span><span>30mi</span>
            </div>
          </div>

          {/* Cost note */}
          <div className="p-3 bg-neutral-900 border border-neutral-800 rounded-xl text-xs text-neutral-500 leading-relaxed">
            <span className="text-neutral-300 font-medium">API cost per scan:</span> ~$0.03 (search) + ~$0.017 × results (details) = roughly <span className="text-white font-medium">$0.30–$0.40 per run</span>. Very cheap.
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-4 bg-red-400/5 border border-red-400/20 rounded-xl">
            <p className="text-xs font-bold text-red-400 mb-1 flex items-center gap-1.5"><AlertTriangle size={12} /> Scan Failed</p>
            <p className="text-xs text-neutral-400">{error}</p>
            <p className="text-xs text-neutral-600 mt-2">Make sure you've run: <code className="text-neutral-400">firebase functions:secrets:set GOOGLE_PLACES_KEY</code></p>
          </div>
        )}

        {/* Results */}
        {result && (
          <div className="mb-6 p-4 bg-emerald-400/5 border border-emerald-400/20 rounded-xl">
            <p className="text-sm font-black text-emerald-400 mb-3">
              ✓ Scan complete — {result.added} new prospect{result.added !== 1 ? 's' : ''} added
            </p>
            <div className="flex gap-4 text-xs text-neutral-400 mb-3">
              <span>Found: <strong className="text-white">{result.total}</strong></span>
              <span>Added: <strong className="text-emerald-400">{result.added}</strong></span>
              <span>Already saved: <strong className="text-neutral-500">{result.skipped}</strong></span>
            </div>
            {result.businesses && result.businesses.length > 0 && (
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {result.businesses.map((b, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="text-neutral-300 truncate flex-1">{b.name}</span>
                    <span className="text-neutral-600 ml-2 shrink-0">{b.distanceMiles}mi</span>
                    {b.website
                      ? <span className="text-emerald-400 ml-2 shrink-0">has site</span>
                      : <span className="text-red-400 ml-2 shrink-0">no site</span>
                    }
                  </div>
                ))}
              </div>
            )}
            <button onClick={handleScan} disabled={scanning} className="mt-3 text-xs text-amber-400 hover:text-amber-300 transition-colors">
              Scan again with different type →
            </button>
          </div>
        )}

        <button
          onClick={handleScan}
          disabled={scanning}
          className="w-full py-3.5 bg-amber-400 hover:bg-amber-300 disabled:opacity-40 disabled:cursor-not-allowed text-black font-black rounded-xl transition-colors flex items-center justify-center gap-2"
        >
          {scanning
            ? <><RefreshCw size={16} className="animate-spin" /> Scanning Google Maps…</>
            : <><ScanLine size={16} /> Find {BUSINESS_TYPES[businessType]?.label || 'Businesses'} Near Yardley</>
          }
        </button>
        {scanning && (
          <p className="text-center text-xs text-neutral-600 mt-2">
            Fetching up to 20 results + details… this takes 15–30 seconds.
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Add Business Modal ───────────────────────────────────────────────────────

function AddBusinessModal({ onClose, onSave }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inputCls = "w-full bg-neutral-900 border border-neutral-700 rounded-xl px-3 py-2.5 text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-amber-400 transition-colors";
  const labelCls = "block text-xs text-neutral-500 uppercase tracking-widest mb-1.5";

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    await onSave({
      id: uid(),
      name: form.name.trim(),
      type: form.type,
      address: form.address.trim(),
      distanceMiles: parseFloat(form.distanceMiles) || null,
      website: form.noWebsite ? null : (form.website.trim() || null),
      instagram: form.noSocial ? null : (form.instagram.trim() || null),
      facebook: form.noSocial ? null : (form.facebook.trim() || null),
      webScore: form.noWebsite ? null : form.webScore,
      socialScore: form.noSocial ? null : form.socialScore,
      contactName: form.contactName.trim(),
      contactEmail: form.contactEmail.trim(),
      notes: form.notes.trim(),
      status: 'pending',
      lastContactDate: null,
    });
    setSaving(false);
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div className="min-h-full max-w-2xl mx-auto px-4 sm:px-6 py-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <span className="text-xs tracking-[0.3em] uppercase text-amber-400 font-medium">Add Prospect</span>
            <h2 className="text-2xl font-black mt-1">New Local Business</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-full bg-neutral-800 hover:bg-neutral-700 transition-colors"><X size={18} /></button>
        </div>

        <div className="mb-6 p-4 bg-amber-400/5 border border-amber-400/20 rounded-xl">
          <p className="text-xs text-amber-400 font-bold uppercase tracking-widest mb-2 flex items-center gap-1.5"><Info size={12} /> How to Find Prospects</p>
          <ol className="text-xs text-neutral-400 space-y-1 list-decimal list-inside">
            <li>Search Google Maps: "restaurants near Yardley PA", "breweries near 19067", etc.</li>
            <li>Click each result — check if they have a website and how it looks</li>
            <li>Search for them on Instagram/Facebook — note follower count and post quality</li>
            <li>Add them here, then use "Analyze Website" in the detail view for a full gap report</li>
          </ol>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Business Name *</label>
              <input required value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="The Corner Tap" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Business Type</label>
              <select value={form.type} onChange={(e) => set('type', e.target.value)} className={inputCls}>
                {Object.entries(BUSINESS_TYPES).map(([k, v]) => (
                  <option key={k} value={k}>{v.emoji} {v.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label className={labelCls}>Address / City</label>
              <input value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="123 Main St, Yardley, PA" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Distance (miles)</label>
              <input type="number" min="0" max="30" step="0.5" value={form.distanceMiles} onChange={(e) => set('distanceMiles', e.target.value)} placeholder="5" className={inputCls} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className={`${labelCls} mb-0`}>Website URL</label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={form.noWebsite} onChange={(e) => set('noWebsite', e.target.checked)} className="accent-amber-400" />
                <span className="text-xs text-neutral-500">No website</span>
              </label>
            </div>
            <input disabled={form.noWebsite} value={form.website} onChange={(e) => set('website', e.target.value)} placeholder="https://their-website.com" className={`${inputCls} disabled:opacity-30 disabled:cursor-not-allowed`} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className={`${labelCls} mb-0`}>Social Media</label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={form.noSocial} onChange={(e) => set('noSocial', e.target.checked)} className="accent-amber-400" />
                <span className="text-xs text-neutral-500">No social</span>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input disabled={form.noSocial} value={form.instagram} onChange={(e) => set('instagram', e.target.value)} placeholder="@instagram_handle" className={`${inputCls} disabled:opacity-30 disabled:cursor-not-allowed`} />
              <input disabled={form.noSocial} value={form.facebook} onChange={(e) => set('facebook', e.target.value)} placeholder="Facebook page URL" className={`${inputCls} disabled:opacity-30 disabled:cursor-not-allowed`} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6 p-4 bg-neutral-900/50 border border-neutral-800 rounded-xl">
            <ScoreRadio label="Website Quality" value={form.webScore} onChange={(v) => set('webScore', v)} disabled={form.noWebsite} />
            <ScoreRadio label="Social Quality" value={form.socialScore} onChange={(v) => set('socialScore', v)} disabled={form.noSocial} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Contact Name</label>
              <input value={form.contactName} onChange={(e) => set('contactName', e.target.value)} placeholder="Owner / Manager name" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Contact Email</label>
              <input type="email" value={form.contactEmail} onChange={(e) => set('contactEmail', e.target.value)} placeholder="owner@business.com" className={inputCls} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Notes</label>
            <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={3} placeholder="What did you notice about their online presence? Any local hooks to mention?" className={`${inputCls} resize-none`} />
          </div>

          <button type="submit" disabled={saving || !form.name.trim()} className="w-full py-3 bg-amber-400 hover:bg-amber-300 text-black font-black rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            {saving ? 'Saving…' : 'Add to Prospect List'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Business Detail / Email Modal ────────────────────────────────────────────

function BusinessModal({ biz, onClose, onStatusChange, onDelete }) {
  const [gaps, setGaps] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [email] = useState(() => generateLocalPitchEmail(biz, null));
  const [editableSubject, setEditableSubject] = useState(email.subject);
  const [editableBody, setEditableBody] = useState(email.body);
  const [copied, setCopied] = useState(null);
  const [contactName, setContactName] = useState(biz.contactName || '');
  const [contactEmail, setContactEmail] = useState(biz.contactEmail || '');
  const [notes, setNotes] = useState(biz.notes || '');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [hunterResults, setHunterResults] = useState(null);
  const [hunterLoading, setHunterLoading] = useState(false);
  const [hunterError, setHunterError] = useState(null);
  const typeInfo = BUSINESS_TYPES[biz.type] || BUSINESS_TYPES.other;

  async function handleAnalyze() {
    if (!biz.website) return;
    setAnalyzing(true);
    try {
      const result = await analyzeWebsiteFn({ url: biz.website });
      const fetchedGaps = result.data;
      setGaps(fetchedGaps);

      // Save any social URLs discovered back to the business record
      if (fetchedGaps.fetchSuccess) {
        const socialUpdate = {};
        if (fetchedGaps.instagramUrl && !biz.instagram) socialUpdate.instagram = fetchedGaps.instagramUrl;
        if (fetchedGaps.facebookUrl  && !biz.facebook)  socialUpdate.facebook  = fetchedGaps.facebookUrl;
        if (Object.keys(socialUpdate).length > 0) {
          await onStatusChange(biz.id, biz.status, socialUpdate);
        }
      }

      // Regenerate the email with the gap intelligence
      const newEmail = generateLocalPitchEmail(biz, fetchedGaps);
      setEditableSubject(newEmail.subject);
      setEditableBody(newEmail.body);
    } catch (err) {
      console.error('Analysis error:', err);
      setGaps({ fetchSuccess: false, errorMsg: err.message });
    }
    setAnalyzing(false);
  }

  async function copyText(text, key) {
    await navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  }

  async function handleStatusChange(newStatus) {
    await onStatusChange(biz.id, newStatus, { contactName, contactEmail, notes });
  }

  async function saveContact() {
    setSaving(true);
    await onStatusChange(biz.id, biz.status, { contactName, contactEmail, notes });
    setSaving(false);
  }

  async function handleFindEmail() {
    if (!biz.website) return;
    setHunterLoading(true);
    setHunterError(null);
    setHunterResults(null);
    try {
      const res = await findBusinessEmailFn({ website: biz.website, businessName: biz.name });
      setHunterResults(res.data);
    } catch (err) {
      setHunterError(err.message || 'Hunter lookup failed.');
    }
    setHunterLoading(false);
  }

  const mailtoLink = `mailto:${contactEmail}?subject=${encodeURIComponent(editableSubject)}&body=${encodeURIComponent(editableBody)}`;

  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div className="min-h-full max-w-3xl mx-auto px-4 sm:px-6 py-8" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <span className="text-xs tracking-[0.3em] uppercase text-amber-400 font-medium">Local Business Outreach</span>
            <h2 className="text-2xl font-black mt-1 flex items-center gap-2">
              <span>{typeInfo.emoji}</span> {biz.name}
            </h2>
            <div className="flex items-center gap-3 mt-1.5 flex-wrap">
              <span className="flex items-center gap-1 text-sm text-neutral-400"><MapPin size={13} /> {biz.address}{biz.distanceMiles ? ` · ${biz.distanceMiles}mi` : ''}</span>
              <StatusBadge status={biz.status} />
              <OpportunityBadge biz={biz} />
            </div>
          </div>
          <button onClick={onClose} className="shrink-0 p-2 rounded-full bg-neutral-800 hover:bg-neutral-700 transition-colors"><X size={18} /></button>
        </div>

        {/* Status actions */}
        <div className="flex flex-wrap gap-2 mb-6">
          {Object.entries(LOCAL_STATUS_CONFIG).map(([s, cfg]) => {
            const isActive = biz.status === s;
            return (
              <button key={s} onClick={() => handleStatusChange(s)}
                className={`px-4 py-2 rounded-full text-xs font-bold tracking-widest uppercase border transition-all ${
                  isActive ? `${cfg.color} ${cfg.bg} ${cfg.border}` : 'border-neutral-700 text-neutral-500 hover:border-neutral-500 hover:text-neutral-300'
                }`}>
                {isActive && <span className="mr-1">✓</span>}{cfg.label}
              </button>
            );
          })}
        </div>

        {/* Web / Social + Analyze button */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-4">
            <p className="text-xs text-neutral-500 uppercase tracking-widest mb-2 flex items-center gap-1.5"><Globe size={11} /> Website</p>
            {biz.website
              ? <><a href={biz.website} target="_blank" rel="noreferrer" className="text-xs text-amber-400 hover:underline truncate block mb-1">{biz.website}</a><ScoreStars value={biz.webScore} /></>
              : <span className="text-xs text-red-400 font-bold">No website found</span>
            }
          </div>
          <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-4">
            <p className="text-xs text-neutral-500 uppercase tracking-widest mb-2 flex items-center gap-1.5"><Globe size={11} /> Social</p>
            {(biz.instagram || biz.facebook)
              ? <><p className="text-xs text-neutral-300 mb-1">{biz.instagram || biz.facebook}</p><ScoreStars value={biz.socialScore} /></>
              : <span className="text-xs text-red-400 font-bold">No social found</span>
            }
          </div>
        </div>

        {/* Analyze Website Button */}
        {biz.website && (
          <button
            onClick={handleAnalyze}
            disabled={analyzing}
            className={`w-full mb-6 flex items-center justify-center gap-2 py-3 rounded-xl border font-bold text-sm transition-all ${
              analyzing
                ? 'border-amber-400/30 text-amber-400/50 bg-amber-400/5 cursor-not-allowed'
                : gaps
                ? 'border-emerald-400/30 text-emerald-400 bg-emerald-400/5 hover:bg-emerald-400/10'
                : 'border-amber-400/40 text-amber-400 bg-amber-400/5 hover:bg-amber-400/10'
            }`}
          >
            {analyzing
              ? <><RefreshCw size={15} className="animate-spin" /> Analyzing website…</>
              : gaps?.fetchSuccess
              ? <><Check size={15} /> Re-analyze website</>
              : <><Search size={15} /> Analyze Website for Gaps</>
            }
          </button>
        )}

        {/* Gap Report */}
        <GapReport gaps={gaps} url={biz.website} />

        {/* Contact + Notes */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
          <div>
            <label className="block text-xs text-neutral-500 uppercase tracking-widest mb-1.5">Contact Name</label>
            <input type="text" value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Owner / manager name" className="w-full bg-neutral-900 border border-neutral-700 rounded-xl px-3 py-2.5 text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-amber-400 transition-colors" />
          </div>
          <div>
            <label className="block text-xs text-neutral-500 uppercase tracking-widest mb-1.5">Contact Email</label>
            <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="owner@business.com" className="w-full bg-neutral-900 border border-neutral-700 rounded-xl px-3 py-2.5 text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-amber-400 transition-colors" />
          </div>
        </div>
        <div className="mb-3">
          <label className="block text-xs text-neutral-500 uppercase tracking-widest mb-1.5">Notes</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="What did you notice? Any personal hooks for the pitch?" className="w-full bg-neutral-900 border border-neutral-700 rounded-xl px-3 py-2.5 text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-amber-400 transition-colors resize-none" />
        </div>
        <button onClick={saveContact} disabled={saving} className="mb-4 text-xs text-neutral-500 hover:text-amber-400 transition-colors disabled:opacity-40">
          {saving ? 'Saving…' : '↑ Save contact & notes'}
        </button>

        {/* Emails found on website */}
        {gaps?.emails?.length > 0 && (
          <div className="mb-6 p-4 bg-emerald-400/5 border border-emerald-400/20 rounded-xl">
            <p className="text-xs font-bold text-emerald-400 uppercase tracking-widest mb-2 flex items-center gap-1.5">
              <Mail size={12} /> {gaps.emails.length} Email{gaps.emails.length !== 1 ? 's' : ''} Found on Website
            </p>
            <div className="flex flex-wrap gap-2">
              {gaps.emails.map((email) => (
                <button
                  key={email}
                  onClick={() => setContactEmail(email)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                    contactEmail === email
                      ? 'bg-emerald-400 text-black border-emerald-400'
                      : 'bg-neutral-900 text-neutral-300 border-neutral-700 hover:border-emerald-400/50 hover:text-emerald-400'
                  }`}
                >
                  {contactEmail === email && <span className="mr-1">✓</span>}
                  {email}
                </button>
              ))}
            </div>
            <p className="text-xs text-neutral-600 mt-2">Click an email to use it as the contact address.</p>
          </div>
        )}

        {/* Hunter.io Email Finder */}
        {biz.website && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs text-neutral-500 uppercase tracking-widest">Email Finder</label>
              <button
                onClick={handleFindEmail}
                disabled={hunterLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-neutral-800 hover:bg-neutral-700 disabled:opacity-40 disabled:cursor-not-allowed border border-neutral-700 hover:border-amber-400/40 rounded-lg text-xs font-medium transition-colors"
              >
                {hunterLoading
                  ? <><RefreshCw size={12} className="animate-spin" /> Searching…</>
                  : <><Search size={12} className="text-amber-400" /> Find with Hunter.io</>
                }
              </button>
            </div>

            {hunterError && (
              <div className="p-3 bg-red-400/5 border border-red-400/20 rounded-xl text-xs text-red-400">
                {hunterError.includes('HUNTER_API_KEY')
                  ? <span>Hunter key not set. Run: <code className="text-neutral-300">firebase functions:secrets:set HUNTER_API_KEY</code></span>
                  : hunterError
                }
              </div>
            )}

            {hunterResults && (
              <div className="p-4 bg-neutral-900 border border-neutral-800 rounded-xl">
                {hunterResults.emails.length === 0 ? (
                  <p className="text-xs text-neutral-500">No emails found in Hunter's database for <span className="text-neutral-300">{hunterResults.domain}</span>.</p>
                ) : (
                  <>
                    <p className="text-xs text-neutral-500 mb-3">
                      {hunterResults.emails.length} result{hunterResults.emails.length !== 1 ? 's' : ''} for <span className="text-neutral-300">{hunterResults.domain}</span>
                      {hunterResults.pattern && <span className="ml-2 text-amber-400/60">pattern: {hunterResults.pattern}</span>}
                    </p>
                    <div className="space-y-2">
                      {hunterResults.emails.map((e) => (
                        <button
                          key={e.email}
                          onClick={() => setContactEmail(e.email)}
                          className={`w-full text-left flex items-center justify-between px-3 py-2 rounded-lg border transition-all text-xs ${
                            contactEmail === e.email
                              ? 'bg-amber-400/10 border-amber-400/40 text-amber-400'
                              : 'bg-neutral-950 border-neutral-800 hover:border-neutral-600 text-neutral-300'
                          }`}
                        >
                          <span className="font-medium">{e.email}</span>
                          <span className="flex items-center gap-3 shrink-0 ml-3">
                            {(e.firstName || e.position) && (
                              <span className="text-neutral-600 hidden sm:inline">
                                {[e.firstName, e.lastName].filter(Boolean).join(' ')}{e.position ? ` · ${e.position}` : ''}
                              </span>
                            )}
                            <span className={`font-bold ${e.confidence >= 80 ? 'text-emerald-400' : e.confidence >= 50 ? 'text-amber-400' : 'text-neutral-500'}`}>
                              {e.confidence}%
                            </span>
                          </span>
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-neutral-700 mt-2">Click an email to use it. % = Hunter's confidence score.</p>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Gap notice if not analyzed yet */}
        {biz.website && !gaps && !analyzing && (
          <div className="mb-4 p-3 bg-amber-400/5 border border-amber-400/20 rounded-xl flex items-start gap-2">
            <Info size={13} className="text-amber-400 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-400/80">
              Hit "Analyze Website" above to scan for gaps — the email will auto-update to specifically call out what they're missing and offer it as a service.
            </p>
          </div>
        )}

        {/* Email Generator */}
        <div className="border border-neutral-800 rounded-2xl overflow-hidden">
          <div className="bg-neutral-900 px-5 py-3 flex items-center justify-between border-b border-neutral-800 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Mail size={15} className="text-amber-400" />
              <span className="text-sm font-bold">
                {gaps?.fetchSuccess ? 'Gap-Targeted Pitch Email' : 'Generated Pitch Email'}
              </span>
              {gaps?.fetchSuccess && (
                <span className="text-xs text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-2 py-0.5 rounded-full">AI-analyzed</span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => copyText(`Subject: ${editableSubject}\n\n${editableBody}`, 'all')}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-neutral-800 hover:bg-neutral-700 rounded-lg text-xs font-medium transition-colors"
              >
                {copied === 'all' ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
                {copied === 'all' ? 'Copied!' : 'Copy All'}
              </button>
              {contactEmail && (
                <a
                  href={mailtoLink}
                  onClick={() => { handleStatusChange('sent'); incrementTodayCount(); }}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-amber-400 hover:bg-amber-300 text-black rounded-lg text-xs font-bold transition-colors"
                >
                  <ExternalLink size={13} /> Open in Mail App
                </a>
              )}
              {!contactEmail && (
                <span className="text-xs text-neutral-600 italic">Add contact email above to send</span>
              )}
            </div>
          </div>

          {/* Subject */}
          <div className="px-5 py-3 bg-neutral-950 border-b border-neutral-800">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <span className="text-xs text-neutral-500 uppercase tracking-widest">Subject</span>
                <input type="text" value={editableSubject} onChange={(e) => setEditableSubject(e.target.value)} className="w-full bg-transparent text-sm text-white mt-0.5 focus:outline-none focus:text-amber-100" />
              </div>
              <button onClick={() => copyText(editableSubject, 'subject')} className="shrink-0 p-1.5 text-neutral-500 hover:text-white transition-colors">
                {copied === 'subject' ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="relative">
            <textarea
              value={editableBody}
              onChange={(e) => setEditableBody(e.target.value)}
              className="w-full px-5 py-5 text-sm text-neutral-300 leading-relaxed bg-transparent focus:outline-none resize-none max-h-[500px] overflow-y-auto font-sans"
              rows={editableBody.split('\n').length + 1}
            />
            <button onClick={() => copyText(editableBody, 'body')} className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 bg-neutral-800/90 hover:bg-neutral-700 rounded-lg text-xs font-medium transition-colors backdrop-blur-sm">
              {copied === 'body' ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
              {copied === 'body' ? 'Copied!' : 'Copy Body'}
            </button>
          </div>
        </div>

        {/* Deliverables reminder */}
        <div className="mt-4 grid grid-cols-3 gap-3">
          {[
            { icon: Film,  label: 'Cinematic Brand Video', sub: '30–60s · 6K · DaVinci' },
            { icon: Zap,   label: '4–15 Social Clips',     sub: 'Reels / TikTok / FB'   },
            { icon: Clock, label: '48hr Turnaround',        sub: 'Social content'         },
          ].map(({ icon: Icon, label, sub }) => (
            <div key={label} className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-3 text-center">
              <Icon size={16} className="text-amber-400 mx-auto mb-1" />
              <p className="text-xs font-bold text-white">{label}</p>
              <p className="text-xs text-neutral-500">{sub}</p>
            </div>
          ))}
        </div>

        {/* Pricing toggle */}
        <button
          onClick={() => setShowPricing((v) => !v)}
          className="mt-4 w-full flex items-center justify-between px-4 py-3 bg-neutral-900 border border-neutral-800 hover:border-neutral-600 rounded-xl transition-colors text-sm font-bold"
        >
          <span className="flex items-center gap-2 text-neutral-300"><BarChart3 size={15} className="text-amber-400" /> View Monthly Pricing Tiers</span>
          <ChevronDown size={16} className={`text-neutral-500 transition-transform ${showPricing ? 'rotate-180' : ''}`} />
        </button>
        <AnimatePresence>
          {showPricing && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} style={{ overflow: 'hidden' }}>
              <PricingPanel />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Delete */}
        <div className="mt-6 pt-6 border-t border-neutral-800">
          {!confirmDelete
            ? <button onClick={() => setConfirmDelete(true)} className="text-xs text-neutral-700 hover:text-red-400 transition-colors">Remove this prospect</button>
            : <div className="flex items-center gap-3">
                <span className="text-xs text-neutral-400">Are you sure?</span>
                <button onClick={() => onDelete(biz.id)} className="text-xs text-red-400 font-bold hover:text-red-300 transition-colors">Yes, delete</button>
                <button onClick={() => setConfirmDelete(false)} className="text-xs text-neutral-500 hover:text-white transition-colors">Cancel</button>
              </div>
          }
        </div>
      </div>
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────

export default function LocalBusinessOutreach() {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('score');
  const [activeBiz, setActiveBiz] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showScan, setShowScan] = useState(false);
  const [todayCount] = useState(getTodayCount());
  const [confirmClearAll, setConfirmClearAll] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const ref = collection(db, 'localBusinessProspects');
        const snap = await getDocs(ref);
        if (snap.empty) {
          await Promise.all(
            LOCAL_BUSINESS_SEED.map((b) =>
              setDoc(doc(db, 'localBusinessProspects', b.id), { ...b, createdAt: serverTimestamp(), updatedAt: serverTimestamp() })
            )
          );
          const fresh = await getDocs(ref);
          setBusinesses(fresh.docs.map((d) => ({ id: d.id, ...d.data() })));
        } else {
          setBusinesses(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        }
      } catch (err) {
        console.error('Firestore load error:', err);
        setLoadError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const handleStatusChange = useCallback(async (bizId, newStatus, extra = {}) => {
    const ref = doc(db, 'localBusinessProspects', bizId);
    await updateDoc(ref, { status: newStatus, updatedAt: serverTimestamp(), ...extra });
    await addDoc(collection(db, 'local_outreach_logs'), { bizId, action: newStatus, ...extra, timestamp: serverTimestamp() });
    setBusinesses((prev) => prev.map((b) => (b.id === bizId ? { ...b, status: newStatus, ...extra } : b)));
    if (activeBiz?.id === bizId) setActiveBiz((prev) => ({ ...prev, status: newStatus, ...extra }));
  }, [activeBiz]);

  const handleAddBusiness = useCallback(async (biz) => {
    await setDoc(doc(db, 'localBusinessProspects', biz.id), { ...biz, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
    setBusinesses((prev) => [biz, ...prev]);
  }, []);

  const handleDelete = useCallback(async (bizId) => {
    await deleteDoc(doc(db, 'localBusinessProspects', bizId));
    setBusinesses((prev) => prev.filter((b) => b.id !== bizId));
    setActiveBiz(null);
  }, []);

  const handleClearAll = useCallback(async () => {
    const snap = await getDocs(collection(db, 'localBusinessProspects'));
    await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
    setBusinesses([]);
    setActiveBiz(null);
    setConfirmClearAll(false);
  }, []);

  // Reload all businesses from Firestore after a scan adds new ones
  const handleScanComplete = useCallback(async () => {
    const snap = await getDocs(collection(db, 'localBusinessProspects'));
    setBusinesses(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  }, []);

  const counts = businesses.reduce((acc, b) => { acc[b.status] = (acc[b.status] || 0) + 1; return acc; }, {});

  const filtered = businesses
    .filter((b) => filter === 'all' || b.status === filter)
    .sort((a, b) => {
      if (sortBy === 'score') return opportunityScore(b) - opportunityScore(a);
      if (sortBy === 'distance') return (a.distanceMiles || 99) - (b.distanceMiles || 99);
      return a.name.localeCompare(b.name);
    });

  return (
    <div className="min-h-screen bg-black text-white pt-16 sm:pt-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">

        {/* Header */}
        <div className="mb-10 flex items-start justify-between gap-4">
          <div>
            <span className="text-xs tracking-[0.3em] uppercase text-amber-400 font-medium">Black Magic Films</span>
            <h1 className="text-4xl sm:text-5xl font-black mt-2 mb-2">Local Business Outreach</h1>
            <p className="text-neutral-400 max-w-xl">
              Find businesses within 30 miles of Yardley with weak online presence. Analyze their website for specific gaps, then send a targeted pitch. Goal: {DAILY_GOAL} outreaches/day.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <div className="flex items-center gap-2">
              <button onClick={() => setShowScan(true)} className="flex items-center gap-2 px-4 py-3 bg-neutral-900 hover:bg-neutral-800 border border-neutral-700 hover:border-amber-400/40 text-white font-bold rounded-xl transition-colors text-sm">
                <ScanLine size={16} className="text-amber-400" /> Scan Google Maps
              </button>
              <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 px-5 py-3 bg-amber-400 hover:bg-amber-300 text-black font-black rounded-xl transition-colors text-sm">
                <Plus size={16} /> Add Prospect
              </button>
            </div>
            {!confirmClearAll ? (
              <button
                onClick={() => setConfirmClearAll(true)}
                disabled={businesses.length === 0}
                className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <Trash2 size={13} /> Clear all {businesses.length} prospects
              </button>
            ) : (
              <div className="flex items-center gap-3">
                <span className="text-xs text-neutral-400">Are you sure? This can't be undone.</span>
                <button onClick={handleClearAll} className="text-xs text-red-400 font-black hover:text-red-300 transition-colors">Yes, delete all</button>
                <button onClick={() => setConfirmClearAll(false)} className="text-xs text-neutral-500 hover:text-white transition-colors">Cancel</button>
              </div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
          <StatCard label="Total Prospects" value={businesses.length}      color="bg-neutral-400" icon={Building2}   />
          <StatCard label="Pitched"         value={counts.sent || 0}       color="bg-blue-400"    icon={Mail}         />
          <StatCard label="Replied"         value={counts.replied || 0}    color="bg-purple-400"  icon={TrendingUp}   />
          <StatCard label="Monthly Clients" value={counts.booked || 0}     color="bg-emerald-400" icon={CheckCircle}  />
        </div>

        {/* Daily Queue */}
        {!loading && !loadError && (
          <DailyQueue businesses={businesses} onOpen={setActiveBiz} todayCount={todayCount} />
        )}

        {/* Filters + Sort */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-2 flex-wrap">
            {FILTERS.map((f) => {
              const cfg = f !== 'all' ? LOCAL_STATUS_CONFIG[f] : null;
              const isActive = filter === f;
              const count = f === 'all' ? businesses.length : (counts[f] || 0);
              return (
                <button key={f} onClick={() => setFilter(f)}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase border transition-all ${
                    isActive
                      ? cfg ? `${cfg.color} ${cfg.bg} ${cfg.border}` : 'bg-white text-black border-white'
                      : 'border-neutral-700 text-neutral-500 hover:border-neutral-500 hover:text-white'
                  }`}>
                  {f === 'all' ? `All (${count})` : `${cfg.label} (${count})`}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            {[['score', 'Opportunity', Target], ['distance', 'Nearest', MapPin], ['name', 'A–Z', ChevronDown]].map(([s, label, Icon]) => (
              <button key={s} onClick={() => setSortBy(s)}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                  sortBy === s ? 'text-amber-400 border-amber-400/30 bg-amber-400/10' : 'text-neutral-500 border-neutral-800 hover:text-white hover:border-neutral-600'
                }`}>
                <Icon size={12} /> {label}
              </button>
            ))}
          </div>
        </div>

        {/* Business grid */}
        {loading ? (
          <div className="text-center py-20 text-neutral-600">Loading prospects…</div>
        ) : loadError ? (
          <div className="text-center py-20">
            <p className="text-red-400 font-bold mb-2">Failed to load prospects</p>
            <p className="text-neutral-500 text-sm font-mono">{loadError}</p>
            <p className="text-neutral-600 text-xs mt-4">Check Firestore rules: <code>firebase deploy --only firestore</code></p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Building2 size={40} className="text-neutral-800 mx-auto mb-4" />
            <p className="text-neutral-500 font-bold mb-2">
              {filter === 'all' ? 'No prospects yet' : `No businesses with status "${LOCAL_STATUS_CONFIG[filter]?.label}"`}
            </p>
            {filter === 'all' && (
              <button onClick={() => setShowAdd(true)} className="mt-4 px-6 py-2.5 bg-amber-400 hover:bg-amber-300 text-black font-black rounded-xl text-sm transition-colors">
                Add Your First Prospect
              </button>
            )}
          </div>
        ) : (
          <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <AnimatePresence>
              {filtered.map((biz) => (
                <BusinessCard key={biz.id} biz={biz} onOpen={setActiveBiz} />
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>

      <AnimatePresence>
        {activeBiz && (
          <BusinessModal
            biz={activeBiz}
            onClose={() => setActiveBiz(null)}
            onStatusChange={handleStatusChange}
            onDelete={handleDelete}
          />
        )}
        {showScan && (
          <ScanModal
            onClose={() => setShowScan(false)}
            onScanComplete={handleScanComplete}
          />
        )}
        {showAdd && (
          <AddBusinessModal
            onClose={() => setShowAdd(false)}
            onSave={handleAddBusiness}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
